import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  Button,
  Paper,
  FormControl,
  FormHelperText,
  OutlinedInput,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/system';
import Sidebar from '../landingPage/Sidebar';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import SalesforceFields from './ConnectionFields/SalesforceFields';
import OracleFields from './ConnectionFields/OracleFields';
import HubSpotFields from './ConnectionFields/HubSpotFields';
import GitHubFields from './ConnectionFields/GitHubFields';
import {
  testConnection,
  getConnectionById,
  updateConnection,
  sqlTestConnection,
  updateHostPort,
} from '../../apis/Connections';
import {
  verifyPasswordForReauth,
  verifyGoogleReauth,
  isGoogleUser,
} from '../../services/reauthService';
import Cookies from 'js-cookie';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchConnections,
  selectConnections,
} from '../../state/slices/connectionsSlice';
import axiosInstance from '../../apis/axiosInstance';
import {
  CONNECTOR_META,
  defaultConnectorMeta,
} from '../../config/connectorMetadata';
import { motion, AnimatePresence } from 'framer-motion';
import { keyframes } from '@mui/material/styles';
import cloudIcon from '../../Images/cloud-conn.png';

const FormSection = styled(Paper)(() => ({
  padding: '1.5rem',
  borderRadius: '16px',
  backgroundColor: '#ffffff',
  border: '1px solid #e5e7eb',
  marginBottom: '1rem',
}));

const StyledOutlinedInput = styled(OutlinedInput)({
  borderRadius: '4px',
  transition: 'all 0.2s ease-in-out',
  height: 36,
  alignItems: 'center',

  '& .MuiOutlinedInput-notchedOutline': {
    border: '0.5px solid #e5e7eb',
  },

  '&:hover .MuiOutlinedInput-notchedOutline': {
    border: '0.5px solid #d1d5db',
  },

  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    border: '0.6px solid #5965A6',
  },
  '&.Mui-error .MuiOutlinedInput-notchedOutline': {
    borderColor: '#ef4444',
    borderWidth: '0.6px',
  },
});

const StyledSelect = styled(Select)({
  '& .MuiSelect-select': {
    padding: '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
    display: 'flex',
    alignItems: 'center',
    height: '100%',
  },
});

const StyledTextField = styled(TextField)({
  '& .MuiOutlinedInput-root': {
    borderRadius: '4px',
    transition: 'all 0.2s ease-in-out',
    height: 36,

    // default border
    '& fieldset': {
      border: '0.5px solid #e5e7eb',
    },

    // hover state
    '&:hover fieldset': {
      border: '0.5px solid #d1d5db',
    },

    // focused state
    '&.Mui-focused fieldset': {
      border: '0.6px solid #5965A6',
    },
    '&.Mui-error .MuiOutlinedInput-notchedOutline': {
      borderColor: '#ef4444',
      borderWidth: '0.6px',
    },
  },
  '& .MuiInputBase-input': {
    padding: '10px 14px',
    fontSize: '0.9rem',
    fontWeight: 400,
    color: '#374151',
  },
  // helper text color when error
  '& .MuiFormHelperText-root.Mui-error': {
    color: '#ef4444',
    fontSize: '0.75rem',
    marginTop: 0.5,
  },
});

const barGrow = keyframes`
  from { transform: translateY(-50%) scaleY(0); opacity: .2; }
  to   { transform: translateY(-50%) scaleY(1); opacity: 1; }
`;

const MotionSectionTitle = styled(motion(Typography))({
  fontSize: '1.1rem',
  fontWeight: 600,
  color: '#1f2937',
  marginBottom: '1rem',
  textTransform: 'capitalize',
  letterSpacing: '0.03em',
  position: 'relative',
  paddingLeft: '12px',
  lineHeight: 1.4,
  '&:before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: '50%',
    width: '4px',
    height: '20px',
    backgroundColor: '#5965A6',
    borderRadius: '2px',
    transform: 'translateY(-50%) scaleY(0)',
    animation: `${barGrow} .50s ease-out forwards`,
  },
});

const containerVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.45,
      ease: 'easeOut',
      when: 'beforeChildren',
      staggerChildren: 0.06,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: 'easeOut' } },
};

const FieldLabel = styled(Typography)({
  fontSize: '0.875rem',
  fontWeight: '500',
  color: '#374151',
  marginBottom: '6px',
});

const StyledButton = styled(Button)(() => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.875rem',
  lineHeight: 1.2,
  padding: '8px 14px',
  minHeight: 36,
  transition:
    'transform .16s ease, box-shadow .2s ease, background-color .2s ease, border-color .2s ease',

  '&:focus-visible': {
    outline: 'none',
    boxShadow: '0 0 0 3px rgba(89,101,166,0.35)',
  },

  // -------- Outlined variant --------
  '&.MuiButton-outlined': {
    backgroundColor: '#EEF2FF',
    color: '#5965A6',
    border: '1px solid rgba(89,101,166,0.20)',
    boxShadow: '0 1px 2px rgba(0,0,0,0.06)',

    '&:hover': {
      backgroundColor: '#E5E9FF',
      borderColor: 'rgba(89,101,166,0.35)',
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 14px rgba(0,0,0,0.12)',
    },
    '&:active': {
      transform: 'translateY(0)',
      backgroundColor: '#DFE4FF',
      boxShadow: '0 3px 8px rgba(0,0,0,0.10)',
    },
  },

  // -------- Contained variant --------
  '&.MuiButton-contained': {
    backgroundColor: '#5A66A6',
    color: '#fff',
    boxShadow: '0 1px 2px rgba(0,0,0,0.08)',

    '&:hover': {
      backgroundColor: '#4C5B9A',
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 14px rgba(0,0,0,0.14)',
    },
    '&:active': {
      transform: 'translateY(0)',
      boxShadow: '0 3px 8px rgba(0,0,0,0.12)',
      backgroundColor: '#44518C',
    },
  },

  '&.Mui-disabled': {
    opacity: 0.6,
    boxShadow: 'none',
    transform: 'none',
  },
}));

const AUTH_TYPES_BY_CONNECTOR = {
  Salesforce: ['OAuth2.0'],
  Oracle: ['Basic Authentication'],
  HubSpot: ['OAuth2.0'],
  GitHub: ['OAuth2.0'],
  JIRA: ['OAuth2.0'],
  sql: ['Basic Authentication'],
};

const ENV_OPTIONS = [
  { value: 'production', label: 'Production' },
  { value: 'test', label: 'Test' },
];

const getErrMsg = (err) =>
  err?.response?.data?.detail ||
  err?.response?.data?.message ||
  err?.detail ||
  err?.error ||
  err?.message ||
  'Connection failed';

const CreateNewConnections = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const connections = useSelector(selectConnections);
  const queryParams = new URLSearchParams(location.search);
  const editingConnId = queryParams.get('conn_id');
  const modeParam = queryParams.get('mode'); // "test" | null

  // 🔍 Modes
  const isTestMode = !!editingConnId && modeParam === 'test';
  const isCreateMode = !editingConnId;

  // --- State Management ---
  const [connector, setConnector] = useState('');
  const [selectedEnv, setSelectedEnv] = useState('');
  const [authType, setAuthType] = useState('');
  const [connectionName, setConnectionName] = useState('');
  const [clientID, setClientID] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [apiVersion, setApiVersion] = useState('');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('');
  const [databaseName, setDatabaseName] = useState('');
  const [ttl, setTtl] = useState('');
  const [instanceUrl, setInstanceUrl] = useState('');
  const [maxInsertRecords, setMaxInsertRecords] = useState('');
  const [errors, setErrors] = useState({});
  const [reauthRequired, setReauthRequired] = useState(false);
  const [reauthPassword, setReauthPassword] = useState('');
  const [reauthError, setReauthError] = useState('');
  const [reauthLoading, setReauthLoading] = useState(false);
  const meta = CONNECTOR_META[connector.toLowerCase()] || defaultConnectorMeta;
  const [sqlTestToken, setSqlTestToken] = useState('');
  const [sqlTroubleshootingOpen, setSqlTroubleshootingOpen] = useState(false);
  const [sqlTroubleshootingSummary, setSqlTroubleshootingSummary] =
    useState('');
  const [sqlTroubleshootingCauses, setSqlTroubleshootingCauses] = useState([]);
  const [newHost, setNewHost] = useState('');
  const [newPort, setNewPort] = useState('');
  const [dialogHostError, setDialogHostError] = useState('');
  const [dialogPortError, setDialogPortError] = useState('');
  const [updatingHostPort, setUpdatingHostPort] = useState(false);

  const isReadOnly = isTestMode;

  const pageTitle = isCreateMode
    ? 'Create New Integration Connection'
    : isTestMode
      ? 'Test Integration Connection'
      : 'Edit Integration Connection';

  const pageSubtitle = isCreateMode
    ? 'Quickly set up a secure connection to integrate with your external systems.'
    : isTestMode
      ? 'Review your existing integration settings and verify that the connection is still valid.'
      : 'Easily update your integration settings and credentials.';

  useEffect(() => {
    const fetchConnectionForEdit = async () => {
      if (!editingConnId) return;

      try {
        const data = await getConnectionById(editingConnId);
        setConnectionName(data.conn_name);
        setConnector(data.connector_name);
        setAuthType(
          data.auth.type === 'OAuth2'
            ? 'OAuth2.0'
            : data.auth.type === 'Basic'
              ? 'Basic Authentication'
              : 'Custom Authentication',
        );
        setApiVersion(data.api_version || '');
        setSelectedEnv(data.environment || '');
        setInstanceUrl(data.custom_params?.instance_url || '');
        setHost(data.custom_params?.host || '');
        setPort(
          data.custom_params?.port !== undefined &&
            data.custom_params?.port !== null
            ? String(data.custom_params.port)
            : '',
        );
        setDatabaseName(
          data.custom_params?.database_name ||
            data.custom_params?.db_name ||
            '',
        );
        setTtl(data.custom_params?.ttl || '');
        setMaxInsertRecords(
          data.custom_params?.max_insert_records !== undefined &&
            data.custom_params?.max_insert_records !== null
            ? String(data.custom_params.max_insert_records)
            : '',
        );

        if (data.auth.oauth2) {
          setClientID(data.auth.oauth2.client_id);
          setClientSecret(data.auth.oauth2.client_secret);
        } else if (data.auth.basic) {
          setUsername(data.auth.basic.username);
          setPassword(data.auth.basic.password);
        } else if (data.auth.custom) {
          setApiKey(data.auth.custom.api_key);
        }

        // Store token used for SQL quick test
        const tokenFromResponse = data?.custom_params?.token || data?.token;

        if (tokenFromResponse) {
          setSqlTestToken(tokenFromResponse);
        }
      } catch (error) {
        toast.error('Failed to load connection');
        console.error('Failed to fetch connection:', error);
      }
    };

    fetchConnectionForEdit();
  }, [editingConnId]);

  // --- This function correctly resets all fields when the connector changes ---
  const handleConnectorChange = (e) => {
    const selected = e.target.value;

    // Reset all form fields to their initial state
    setSelectedEnv('');
    setConnectionName('');
    setClientID('');
    setClientSecret('');
    setUsername('');
    setPassword('');
    setApiKey('');
    setApiVersion('');
    setHost('');
    setPort('');
    setDatabaseName('');
    setInstanceUrl('');
    setErrors({});

    // Set the new connector and its default auth type
    setConnector(selected);
    setAuthType(AUTH_TYPES_BY_CONNECTOR[selected]?.[0] || '');
  };

  // const validateForm = () => {
  //   const newErrors = {};
  //   if (!connector) newErrors.connector = 'Connector is required';
  //   if (!selectedEnv) newErrors.environment = 'Environment is required';
  //   if (!authType) newErrors.authType = 'Authentication Type is required';
  //   if (!connectionName.trim())
  //     newErrors.connectionName = 'Connection Name is required';

  //   if (authType === 'OAuth2.0') {
  //     if (!clientID.trim()) newErrors.clientID = 'Client ID is required';
  //     if (!clientSecret.trim())
  //       newErrors.clientSecret = 'Client Secret is required';
  //   } else if (authType === 'Basic Authentication') {
  //     if (!username.trim()) newErrors.username = 'Username is required';
  //     if (!password.trim()) newErrors.password = 'Password is required';
  //     // SQL-specific: require host/port/database, ignore API version
  //     if (connector === 'sql') {
  //       if (!host.trim()) newErrors.host = 'Host is required';
  //       if (!port.trim()) newErrors.port = 'Port is required';
  //       if (ttl === '' || ttl === null || ttl === undefined)
  //         newErrors.ttl = 'TTL is required';
  //       if (port && !/^\d+$/.test(port))
  //         newErrors.port = 'Port must be a number';
  //       if (!databaseName.trim())
  //         newErrors.databaseName = 'Database name is required';
  //     }
  //   } else if (authType === 'Custom Authentication' && !apiKey.trim()) {
  //     newErrors.apiKey = 'API Key is required';
  //   }

  //   // Only validate API version if NOT SQL Basic and meta requires it
  //   const meta =
  //     CONNECTOR_META[connector.toLowerCase()] || defaultConnectorMeta;
  //   const isSqlBasic =
  //     connector === 'sql' && authType === 'Basic Authentication';

  //   if (
  //     !isSqlBasic &&
  //     connector !== 'JIRA' &&
  //     meta.requiresApiVersion &&
  //     !apiVersion.trim()
  //   ) {
  //     newErrors.apiVersion = 'API Version is required';
  //   }

  //   // Only validate Instance URL if the connector is not JIRA
  //   if (
  //     connector !== 'JIRA' &&
  //     meta.requiresInstanceUrl &&
  //     !instanceUrl.trim()
  //   ) {
  //     newErrors.instanceUrl = 'Instance URL is required';
  //   }

  //   setErrors(newErrors);
  //   return Object.keys(newErrors).length === 0;
  // };

  const validateForm = () => {
    const newErrors = {};

    // ---- common required ----
    if (!connector) newErrors.connector = 'Connector is required';
    if (!selectedEnv) newErrors.environment = 'Environment is required';
    if (!connectionName.trim())
      newErrors.connectionName = 'Connection Name is required';

    const key = (connector || '').toLowerCase();
    const at = authType || '';

    // Small helpers
    const hasValue = (v) => typeof v === 'string' && v.trim().length > 0;
    const requireUrl = (url, fieldKey) => {
      if (!hasValue(url)) {
        newErrors[fieldKey] = 'Instance URL is required';
        return;
      }
      try {
        // Will throw if invalid
        // Allow host-only like "https://login.salesforce.com", "https://my.oraclecloud.com"
        new URL(url);
      } catch {
        newErrors[fieldKey] = 'Enter a valid URL (e.g., https://domain.tld)';
      }
    };
    const requireNumber = (val, fieldKey, label) => {
      if (val === '' || val === null || val === undefined) {
        newErrors[fieldKey] = `${label} is required`;
        return;
      }
      if (!/^\d+$/.test(String(val))) {
        newErrors[fieldKey] = `${label} must be a number`;
      }
    };

    // ---- per-connector rules ----
    switch (key) {
      case 'salesforce': {
        // OAuth2.0 with client id/secret + Instance URL
        if (at !== 'OAuth2.0') {
          newErrors.authType = 'Salesforce uses OAuth2.0';
          break;
        }
        if (!hasValue(clientID)) newErrors.clientID = 'Client ID is required';
        if (!hasValue(clientSecret))
          newErrors.clientSecret = 'Client Secret is required';
        requireUrl(instanceUrl, 'instanceUrl'); // e.g., https://login.salesforce.com or custom domain
        break;
      }

      case 'oracle': {
        // Basic Auth with username/password + Instance URL
        if (at !== 'Basic Authentication') {
          newErrors.authType = 'Oracle uses Basic Authentication';
          break;
        }
        if (!hasValue(username)) newErrors.username = 'Username is required';
        if (!hasValue(password)) newErrors.password = 'Password is required';
        requireUrl(instanceUrl, 'instanceUrl'); // e.g., https://my.oraclecloud.com/...
        break;
      }

      case 'sql': {
        // Basic Auth with username/password + Host/Port/TTL/DB Name (no instance URL)
        if (at !== 'Basic Authentication') {
          newErrors.authType = 'SQL Server uses Basic Authentication';
          break;
        }
        if (!hasValue(username)) newErrors.username = 'Username is required';
        if (!hasValue(password)) newErrors.password = 'Password is required';

        if (!hasValue(host)) newErrors.host = 'Host is required';
        requireNumber(port, 'port', 'Port');
        // Optional: ensure port range
        if (!newErrors.port) {
          const p = Number(port);
          if (p < 1 || p > 65535)
            newErrors.port = 'Port must be between 1 and 65535';
        }

        // TTL required (select value)
        if (ttl === '' || ttl === null || ttl === undefined)
          newErrors.ttl = 'TTL is required';

        if (!hasValue(databaseName))
          newErrors.databaseName = 'Database name is required';
        break;
      }

      case 'hubspot': {
        // OAuth2.0 with client id/secret (no instance URL required)
        if (at !== 'OAuth2.0') {
          newErrors.authType = 'HubSpot uses OAuth2.0';
          break;
        }
        if (!hasValue(clientID)) newErrors.clientID = 'Client ID is required';
        if (!hasValue(clientSecret))
          newErrors.clientSecret = 'Client Secret is required';

        // Instance URL optional for HubSpot, but if user enters, validate it
        if (hasValue(instanceUrl)) {
          requireUrl(instanceUrl, 'instanceUrl');
        }
        break;
      }

      case 'github': {
        // OAuth2.0 with client id/secret (no instance URL required)
        if (at !== 'OAuth2.0') {
          newErrors.authType = 'GitHub uses OAuth2.0';
          break;
        }
        if (!hasValue(clientID)) newErrors.clientID = 'Client ID is required';
        if (!hasValue(clientSecret))
          newErrors.clientSecret = 'Client Secret is required';

        // Instance URL optional for GitHub, but if user enters, validate it
        if (hasValue(instanceUrl)) {
          requireUrl(instanceUrl, 'instanceUrl');
        }
        break;
      }

      case 'jira': {
        // OAuth2.0 with client id/secret (no instance URL)
        if (at !== 'OAuth2.0') {
          newErrors.authType = 'JIRA uses OAuth2.0';
          break;
        }
        if (!hasValue(clientID)) newErrors.clientID = 'Client ID is required';
        if (!hasValue(clientSecret))
          newErrors.clientSecret = 'Client Secret is required';
        // explicitly DO NOT require instanceUrl for JIRA
        break;
      }

      default: {
        // Fallback: honor current selection minimally
        if (!hasValue(at))
          newErrors.authType = 'Authentication Type is required';
        if (at === 'OAuth2.0') {
          if (!hasValue(clientID)) newErrors.clientID = 'Client ID is required';
          if (!hasValue(clientSecret))
            newErrors.clientSecret = 'Client Secret is required';
        } else if (at === 'Basic Authentication') {
          if (!hasValue(username)) newErrors.username = 'Username is required';
          if (!hasValue(password)) newErrors.password = 'Password is required';
        } else if (at === 'Custom Authentication') {
          if (!hasValue(apiKey)) newErrors.apiKey = 'API Key is required';
        }
        break;
      }
    }

    setErrors(newErrors);
    // If there are validation errors, show a toast notification so the user
    // immediately understands that some fields need attention.  We aggregate
    // all error messages into a single string separated by pipes.  You can
    // adjust the delimiter if you prefer a different separator.
    if (Object.keys(newErrors).length > 0) {
      const messages = Object.values(newErrors).join(' | ');
      toast.error(messages || 'Please fix the highlighted errors.', {
        autoClose: 3000,
      });
    }
    return Object.keys(newErrors).length === 0;
  };

  const connectorKey = connector.toLowerCase();
  const isSqlLikeConnector =
    connectorKey === 'sql' ||
    connectorKey === 'postgres' ||
    connectorKey === 'postgresql';
  // const buildConnectionPayload = () => {
  //   const meta = CONNECTOR_META[connectorKey] || defaultConnectorMeta;
  //   const redirectUri =
  //     connectorKey === 'jira'
  //       ? 'https://hmorchestrator.hyperminds.tech/api/v1/jira/oauth/callback'
  //       : `${window.location.origin}/oauth/callback`;

  //   return {
  //     user_id: Cookies.get('userId'),
  //     conn_name: connectionName,
  //     connector_name: connector,
  //     api_version: meta.requiresApiVersion ? apiVersion : null,
  //     custom_params: {
  //       instance_url: meta.requiresInstanceUrl ? instanceUrl : null,
  //     },
  //     auth: {
  //       type:
  //         authType === 'OAuth2.0'
  //           ? 'OAuth2'
  //           : authType === 'Basic Authentication'
  //           ? 'Basic'
  //           : 'Custom',
  //       ...(authType === 'OAuth2.0' && {
  //         oauth2: {
  //           grant_type: 'authorization_code',
  //           client_id: clientID,
  //           client_secret: clientSecret,
  //           instance_url: connectorKey === 'jira' ? null : instanceUrl,
  //           redirect_uri: redirectUri,
  //         },
  //       }),
  //       ...(authType === 'Basic Authentication' && {
  //         basic: { username, password, instance_url: instanceUrl },
  //       }),
  //       ...(authType === 'Custom Authentication' && {
  //         custom: { api_key: apiKey, instance_url: instanceUrl },
  //       }),
  //     },
  //     environment: selectedEnv,
  //     ...(connectorKey === 'jira' && {
  //       custom_params: { project_key: 'ECS' },
  //     }),
  //   };
  // };

  const buildConnectionPayload = () => {
    const meta = CONNECTOR_META[connectorKey] || defaultConnectorMeta;

    const redirectUri =
      connectorKey === 'jira'
        ? 'https://hmorchestrator.hyperminds.tech/api/v1/jira/oauth/callback'
        : connectorKey === 'hubspot'
          ? `${window.location.origin}/myconnections`
          : connectorKey === 'github'
            ? `${window.location.origin}/github/oauth/callback`
            : `${window.location.origin}/oauth/callback`;

    // Base custom params
    let customParams = meta.requiresInstanceUrl
      ? { instance_url: instanceUrl }
      : {};

    // SQL-specific custom params
    if (connectorKey === 'sql') {
      customParams = {
        host: host,
        port: port,
        database_name: databaseName,
        ttl: ttl,
      };
    }

    // Jira-specific custom params
    if (connectorKey === 'jira') {
      customParams = { project_key: 'ECS' };
    }

    // Add max_insert_records to custom_params for update mode (all connectors)
    // Always include it in update mode - send null if cleared, or the parsed value if provided
    if (!isCreateMode) {
      if (maxInsertRecords && maxInsertRecords.trim() !== '') {
        const parsedValue = parseInt(maxInsertRecords.trim(), 10);
        if (!isNaN(parsedValue)) {
          customParams = {
            ...customParams,
            max_insert_records: parsedValue,
          };
        } else {
          // Invalid number - send null to clear it
          customParams = {
            ...customParams,
            max_insert_records: null,
          };
        }
      } else {
        // Field is empty/cleared - explicitly send null to update database
        customParams = {
          ...customParams,
          max_insert_records: null,
        };
      }
    }

    const payload = {
      user_id: Cookies.get('userId'),
      conn_name: connectionName,
      connector_name: connector,
      api_version: meta.requiresApiVersion ? apiVersion : null,
      custom_params: customParams,
      auth: {
        type:
          authType === 'OAuth2.0'
            ? 'OAuth2'
            : authType === 'Basic Authentication'
              ? 'Basic'
              : 'Custom',

        // OAuth2 config
        ...(authType === 'OAuth2.0' && {
          oauth2: {
            grant_type: 'authorization_code',
            client_id: clientID,
            client_secret: clientSecret,
            instance_url: connectorKey === 'jira' ? null : instanceUrl,
            redirect_uri: redirectUri,
          },
        }),

        // Basic Auth config
        ...(authType === 'Basic Authentication' && {
          basic:
            connectorKey === 'sql'
              ? { username, password } // SQL does NOT need instance_url
              : { username, password, instance_url: instanceUrl }, // Oracle and others
        }),

        // Custom Auth config
        ...(authType === 'Custom Authentication' && {
          custom: { api_key: apiKey, instance_url: instanceUrl },
        }),
      },
      environment: selectedEnv,
    };

    // For create mode, add max_insert_records below conn_name if it has a value
    if (isCreateMode && maxInsertRecords && maxInsertRecords.trim() !== '') {
      const parsedValue = parseInt(maxInsertRecords.trim(), 10);
      if (!isNaN(parsedValue)) {
        payload.max_insert_records = parsedValue;
      }
    }

    return payload;
  };

  const checkConnectorLimit = async () => {
    if (!isCreateMode) return true;

    const trialStatusStr = Cookies.get('trialStatus');
    if (!trialStatusStr) return true;

    let trialStatus;
    try {
      trialStatus = JSON.parse(trialStatusStr);
    } catch (e) {
      console.error('Failed to parse trial status:', e);
      return true;
    }

    if (trialStatus.is_paid) return true;

    if (!trialStatus.connectors_limit_enabled) return true;

    try {
      const result = await dispatch(fetchConnections()).unwrap();
      const activeCount = Array.isArray(result)
        ? result.length
        : connections.length;
      const maxConnectors = trialStatus.max_connectors || 5;

      if (activeCount >= maxConnectors) {
        toast.error(
          `Connector limit reached. Maximum ${maxConnectors} active connectors allowed. Please delete unused connectors.`,
          { autoClose: 5000 },
        );
        return false;
      }
    } catch (error) {
      console.error('Error checking connector limit:', error);
      const activeCount = connections.length;
      const maxConnectors = trialStatus.max_connectors || 5;
      if (activeCount >= maxConnectors) {
        toast.error(
          `Connector limit reached. Maximum ${maxConnectors} active connectors allowed. Please delete unused connectors.`,
          { autoClose: 5000 },
        );
        return false;
      }
      return true;
    }

    return true;
  };

  const handleTestConnection = async () => {
    if (!validateForm()) return;

    if (isCreateMode) {
      const canCreate = await checkConnectorLimit();
      if (!canCreate) {
        return;
      }
    }

    const payload = buildConnectionPayload();
    try {
      const response = await testConnection(payload);

      if (response?.detail || response?.error || response?.message) {
        const msg = response.detail || response.error || response.message;
        toast.error(`Test failed: ${msg}`, { autoClose: 3000 });
        return navigate('/myconnections', {
          replace: true,
          state: { errorMessage: msg },
        });
      }

      if (response?.test_status || response?.auth?.oauth2?.access_token) {
        toast.success('Test connection successful!', { autoClose: 2000 });
        try {
          const token = Cookies.get('authToken');
          if (token) {
            const trialResponse = await axiosInstance.get('/auth/user', {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (trialResponse.data.trial_status) {
              Cookies.set(
                'trialStatus',
                JSON.stringify(trialResponse.data.trial_status),
                {
                  expires: 1,
                  secure: true,
                },
              );
            }
          }
        } catch (trialError) {
          console.error('Failed to refresh trial status:', trialError);
        }
        setTimeout(() => navigate('/myconnections'), 2000);
      } else if (response?.auth?.oauth2?.auth_url) {
        const redirectUri = payload.auth.oauth2.redirect_uri;

        // Check if it's Jira or not and apply the relevant logic
        const authUrl = response.auth.oauth2.auth_url;
        console.log('auth url:::', authUrl);
        const url = new URL(authUrl);

        console.log('url url:::', url);

        if (
          connectorKey.toLowerCase() === 'jira' ||
          connectorKey.toLowerCase() === 'hubspot' ||
          connectorKey.toLowerCase() === 'github'
        ) {
          // Jira/HubSpot/GitHub: Only append redirect_uri if it's not already present
          if (!url.searchParams.has('redirect_uri')) {
            window.open(
              `${authUrl}&redirect_uri=${encodeURIComponent(redirectUri)}`,
              '_self',
            );
          } else {
            window.open(authUrl, '_self');
          }
        } else {
          // Salesforce and other OAuth connectors
          const pending = {
            connectionName,
            connector,
            clientID,
            clientSecret,
            instanceUrl,
            redirectUri: payload.auth.oauth2.redirect_uri,
            authType: 'OAuth2',
            environment: selectedEnv,
          };
          console.log('saving pendingOAuthConnection:', pending);
          sessionStorage.setItem(
            'pendingOAuthConnection',
            JSON.stringify(pending),
          );

          // Create URL object from auth_url
          const urlObj = new URL(authUrl);
          
          // Ensure response_type is set to 'code' for Salesforce OAuth (required parameter)
          if (!urlObj.searchParams.has('response_type')) {
            urlObj.searchParams.set('response_type', 'code');
          }
          
          // Only append redirect_uri if it's not already present
          if (!urlObj.searchParams.has('redirect_uri')) {
            urlObj.searchParams.set('redirect_uri', redirectUri);
          }

          // Add state parameter with environment and timestamp
          const statePayload = { env: selectedEnv, t: Date.now() };
          urlObj.searchParams.set('state', btoa(JSON.stringify(statePayload)));

          console.log('CREATE opening OAuth URL:', urlObj.toString());
          console.log('URL search params:', Object.fromEntries(urlObj.searchParams));

          window.open(urlObj.toString(), '_self');

          // window.open(
          //   `${authUrl}&redirect_uri=${encodeURIComponent(redirectUri)}`,
          //   '_self'
          // );
        }
      } else {
        toast.warn('Test partially completed. Check response.', {
          autoClose: 3000,
        });
      }
    } catch (err) {
      const message = getErrMsg(err);
      toast.error(`Test failed: ${message}`, { autoClose: 3000 });
      navigate('/myconnections', {
        replace: true,
        state: { errorMessage: message },
      });
    }
  };

  const handleUpdateConnection = async () => {
    if (!validateForm()) return;

    // If reauthentication is required, handle it first
    if (reauthRequired) {
      await handleReauthAndUpdate();
      return;
    }

    // Check if user needs reauthentication
    if (isGoogleUser()) {
      // For Google users, trigger popup reauthentication
      try {
        setReauthLoading(true);
        const result = await verifyGoogleReauth();
        if (result.success) {
          await performUpdate();
        } else {
          toast.error('Reauthentication failed. Please try again.');
        }
      } catch (error) {
        toast.error('Reauthentication failed. Please try again.');
      } finally {
        setReauthLoading(false);
      }
    } else {
      // For password users, show password field
      setReauthRequired(true);
    }
  };

  const handleReauthAndUpdate = async () => {
    if (!reauthPassword.trim()) {
      setReauthError('Password is required');
      return;
    }

    setReauthLoading(true);
    setReauthError('');

    try {
      const result = await verifyPasswordForReauth(reauthPassword);
      if (result.success) {
        await performUpdate();
      } else {
        setReauthError(result.error);
      }
    } catch (error) {
      setReauthError('Reauthentication failed. Please try again.');
    } finally {
      setReauthLoading(false);
    }
  };

  const performUpdate = async () => {
    const payload = buildConnectionPayload();
    try {
      await updateConnection(editingConnId, payload);
      toast.success('Connection updated successfully!');
      navigate('/myconnections');
    } catch (err) {
      toast.error('Failed to update connection');
    }
  };

  const handleSqlTestConnection = async () => {
    if (!isTestMode) {
      toast.error('Quick Test is only available in Test mode.', {
        autoClose: 3000,
      });
      return;
    }

    if (!isSqlLikeConnector) {
      toast.error(
        'Quick Test is only supported for SQL / Postgres connectors.',
        {
          autoClose: 3000,
        },
      );
      return;
    }

    if (!sqlTestToken) {
      toast.error(
        'Quick Test token not found. Please re-save this connection or contact admin.',
        { autoClose: 4000 },
      );
      return;
    }

    try {
      toast.info('Running quick SQL connection test…', { autoClose: 1500 });

      const res = await sqlTestConnection(sqlTestToken);

      const isSuccess =
        res?.status === 'success' ||
        res?.ok === true ||
        res?.test_status === 'success' ||
        res?.connected === true;

      if (isSuccess) {
        toast.success('SQL connection is healthy!', { autoClose: 2500 });
      } else {
        const msg =
          res?.message || res?.detail || res?.error || 'SQL test failed';
        toast.error(`Quick Test failed: ${msg}`, { autoClose: 4000 });

        const t = res?.troubleshooting;

        const summaryFromServer =
          t?.error_type === 'driver_not_found'
            ? 'The SQL driver could not connect using the current host/port.'
            : t?.note || msg;

        const causes = Array.isArray(t?.possible_causes)
          ? t.possible_causes
          : [];

        // Pre-fill suggested host/port
        const suggestedHost =
          res?.host ||
          t?.additional_info?.connection_details?.host ||
          host ||
          '';
        const suggestedPortRaw =
          t?.additional_info?.connection_details?.port ?? port ?? '';
        const suggestedPort =
          suggestedPortRaw !== null && suggestedPortRaw !== undefined
            ? String(suggestedPortRaw)
            : '';

        setSqlTroubleshootingSummary(
          summaryFromServer ||
            'Connection failed. Please review the details below.',
        );
        setSqlTroubleshootingCauses(causes);
        setNewHost(suggestedHost);
        setNewPort(suggestedPort);
        setDialogHostError('');
        setDialogPortError('');
        setSqlTroubleshootingOpen(true);
      }
    } catch (err) {
      const msg = getErrMsg(err);
      toast.error(`Quick Test failed: ${msg}`, { autoClose: 4000 });
    }
  };

  const handleUpdateHostPort = async () => {
    setDialogHostError('');
    setDialogPortError('');

    const trimmedHost = (newHost || '').trim();
    const trimmedPort = (newPort || '').trim();

    let hasError = false;

    if (!trimmedHost) {
      setDialogHostError('Host is required');
      hasError = true;
    }

    if (!trimmedPort) {
      setDialogPortError('Port is required');
      hasError = true;
    } else if (!/^\d+$/.test(trimmedPort)) {
      setDialogPortError('Port must be a number');
      hasError = true;
    }

    if (hasError) return;

    if (!editingConnId) {
      toast.error('Missing connection id to update host/port.');
      return;
    }

    try {
      setUpdatingHostPort(true);

      await updateHostPort(editingConnId, {
        host: trimmedHost,
        port: trimmedPort,
      });

      // Sync into the main form state so it stays consistent
      setHost(trimmedHost);
      setPort(trimmedPort);

      toast.success('Host and port updated successfully.', {
        autoClose: 2500,
      });
      setSqlTroubleshootingOpen(false);
    } catch (err) {
      const msg = getErrMsg(err);
      toast.error(`Failed to update host/port: ${msg}`, { autoClose: 4000 });
    } finally {
      setUpdatingHostPort(false);
    }
  };

  const renderConnectorFields = () => {
    switch (connector) {
      case 'Salesforce':
        return (
          <SalesforceFields
            instanceUrl={instanceUrl}
            setInstanceUrl={setInstanceUrl}
            error={{ instanceUrl: errors.instanceUrl || '' }}
          />
        );
      case 'Oracle':
        return (
          <OracleFields
            instanceUrl={instanceUrl}
            setInstanceUrl={setInstanceUrl}
            error={{ instanceUrl: errors.instanceUrl || '' }}
          />
        );
      case 'HubSpot':
        return (
          <HubSpotFields
            instanceUrl={instanceUrl}
            setInstanceUrl={setInstanceUrl}
            error={{ instanceUrl: errors.instanceUrl || '' }}
          />
        );
      case 'GitHub':
        return (
          <GitHubFields
            instanceUrl={instanceUrl}
            setInstanceUrl={setInstanceUrl}
            error={{ instanceUrl: errors.instanceUrl || '' }}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: 'auto',
        backgroundColor: '#f8fafc',
        p: { xs: 2, md: 3 },
      }}
    >
      <Sidebar />
      <Box
        component="main"
        sx={{
          position: 'relative',
          flexGrow: 1,
          // overflow:'hidden',
          px: { xs: 2, md: 3 },
          backgroundColor: '#EAF0FF80',
          borderRadius: '8px',
          pb: '1rem',
        }}
      >
        {/* Decorative layers */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          {/* Bottom-left shape */}
          <Box
            component="img"
            src={require('../../Images/Deco-left.png')}
            alt=""
            sx={{
              position: 'absolute',
              left: { xs: -100, md: -140 },
              bottom: { xs: -100, md: -160 },
              width: { xs: 280, md: 480 },
              opacity: 0.55,
              filter: 'drop-shadow(0 6px 18px rgba(0,0,0,0.25))',
              userSelect: 'none',
            }}
          />

          {/* Top-right shape */}
          <Box
            component="img"
            src={require('../../Images/Deco-right.png')}
            alt=""
            sx={{
              position: 'absolute',
              top: { xs: -80, md: -120 },
              right: { xs: -100, md: -160 },
              width: { xs: 300, md: 520 },
              opacity: 0.55,
              userSelect: 'none',
            }}
          />
        </Box>

        <Box
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          sx={{
            maxWidth: { xs: '100%', sm: '640px', md: '860px', lg: '1200px' },
            mx: 'auto',
            mt: 1,
            position: 'relative',
            zIndex: 1,
            px: { xs: 2, sm: 3 },
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <Box
              component="img"
              src={cloudIcon}
              alt="Connection Icon"
              sx={{ width: 32, height: 32 }}
            />
            <Typography
              component={motion.h1}
              variants={itemVariants}
              variant="h6"
              sx={{
                mt: 8,
                mb: 0.5,
                fontWeight: '600',
                color: '#111827',
                fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' },
              }}
            >
              {pageTitle}
            </Typography>
          </Box>

          <Typography
            component={motion.p}
            variants={itemVariants}
            variant="body1"
            sx={{
              color: '#6b7280',
              mb: 2.5,
              fontSize: '0.9rem',
            }}
          >
            {pageSubtitle}
          </Typography>

          <FormSection>
            {/* Basic Configuration */}
            <MotionSectionTitle
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
            >
              Basic Configuration
            </MotionSectionTitle>

            <Box
              component={motion.div}
              variants={itemVariants}
              display="grid"
              gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
              gap={2}
              mb={2}
            >
              <FormControl fullWidth error={!!errors.connector}>
                <FieldLabel>Connector Type</FieldLabel>
                <StyledSelect
                  value={connector}
                  input={<StyledOutlinedInput />}
                  onChange={handleConnectorChange}
                  displayEmpty
                  disabled={isReadOnly}
                >
                  <MenuItem value="" disabled>
                    <em>Choose a connector</em>
                  </MenuItem>
                  <MenuItem value="Salesforce">Salesforce</MenuItem>
                  <MenuItem value="sql">SQL Server</MenuItem>
                  <MenuItem value="Oracle">Oracle</MenuItem>
                  <MenuItem value="HubSpot">HubSpot</MenuItem>
                  <MenuItem value="GitHub">GitHub</MenuItem>
                  <MenuItem value="JIRA">Atlassian JIRA</MenuItem>
                </StyledSelect>
                <FormHelperText
                  sx={{ color: '#ef4444', fontSize: '0.75rem', mt: 0.5 }}
                >
                  {errors.connector}
                </FormHelperText>
              </FormControl>

              <FormControl fullWidth error={!!errors.environment}>
                <FieldLabel>Environment</FieldLabel>
                <StyledSelect
                  value={selectedEnv}
                  input={<StyledOutlinedInput />}
                  onChange={(e) => setSelectedEnv(e.target.value)}
                  displayEmpty
                  disabled={isReadOnly}
                >
                  <MenuItem value="" disabled>
                    <em>Select environment</em>
                  </MenuItem>
                  {ENV_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </StyledSelect>
                <FormHelperText
                  sx={{ color: '#ef4444', fontSize: '0.75rem', mt: 0.5 }}
                >
                  {errors.environment}
                </FormHelperText>
              </FormControl>
            </Box>

            <Box
              component={motion.div}
              variants={itemVariants}
              display="grid"
              gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
              gap={2}
              mb={3}
            >
              <FormControl fullWidth>
                <FieldLabel>Connection Name</FieldLabel>
                <StyledTextField
                  placeholder="Enter a descriptive name"
                  value={connectionName}
                  onChange={(e) => setConnectionName(e.target.value)}
                  error={!!errors.connectionName}
                  helperText={errors.connectionName}
                  InputProps={isReadOnly ? { readOnly: true } : undefined}
                />
              </FormControl>

              <FormControl fullWidth error={!!errors.authType}>
                <FieldLabel>Authentication Method</FieldLabel>
                {['Salesforce', 'Oracle', 'JIRA'].includes(connector) ? (
                  <StyledTextField
                    value={authType}
                    InputProps={{ readOnly: true }}
                  />
                ) : (
                  <StyledSelect
                    value={authType}
                    input={<StyledOutlinedInput />}
                    onChange={(e) => setAuthType(e.target.value)}
                    displayEmpty
                    disabled={isReadOnly}
                  >
                    <MenuItem value="" disabled>
                      <em>Select authentication type</em>
                    </MenuItem>
                    {(AUTH_TYPES_BY_CONNECTOR[connector] || []).map((type) => (
                      <MenuItem key={type} value={type}>
                        {type}
                      </MenuItem>
                    ))}
                  </StyledSelect>
                )}
                <FormHelperText
                  sx={{ color: '#ef4444', fontSize: '0.75rem', mt: 0.5 }}
                >
                  {errors.authType}
                </FormHelperText>
              </FormControl>
            </Box>

            <AnimatePresence initial={false} mode="wait">
              {/* Authentication Details */}
              {authType && (
                <motion.div
                  key={`auth-${authType}`}
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                >
                  <MotionSectionTitle
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                  >
                    Authentication Details
                  </MotionSectionTitle>

                  {authType === 'OAuth2.0' && (
                    <Box
                      display="grid"
                      gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                      gap={2}
                      mb={3}
                    >
                      <FormControl fullWidth>
                        <FieldLabel>Client ID</FieldLabel>
                        <StyledTextField
                          placeholder="Enter client ID"
                          value={clientID}
                          onChange={(e) => setClientID(e.target.value)}
                          error={!!errors.clientID}
                          helperText={errors.clientID}
                          InputProps={
                            isReadOnly ? { readOnly: true } : undefined
                          }
                        />
                      </FormControl>
                      <FormControl fullWidth>
                        <FieldLabel>Client Secret</FieldLabel>
                        <StyledTextField
                          type="password"
                          placeholder="Enter client secret"
                          value={clientSecret}
                          onChange={(e) => setClientSecret(e.target.value)}
                          error={!!errors.clientSecret}
                          helperText={errors.clientSecret}
                          InputProps={
                            isReadOnly ? { readOnly: true } : undefined
                          }
                        />
                      </FormControl>
                    </Box>
                  )}

                  {authType === 'Basic Authentication' && (
                    <Box
                      display="grid"
                      gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                      gap={2}
                      mb={3}
                    >
                      <FormControl fullWidth>
                        <FieldLabel>Username</FieldLabel>
                        <StyledTextField
                          placeholder="Enter username"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          error={!!errors.username}
                          helperText={errors.username}
                          InputProps={
                            isReadOnly ? { readOnly: true } : undefined
                          }
                        />
                      </FormControl>
                      <FormControl fullWidth>
                        <FieldLabel>Password</FieldLabel>
                        <StyledTextField
                          type="password"
                          placeholder="Enter password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          error={!!errors.password}
                          helperText={errors.password}
                          InputProps={
                            isReadOnly ? { readOnly: true } : undefined
                          }
                        />
                      </FormControl>
                    </Box>
                  )}

                  {authType === 'Custom Authentication' && (
                    <Box mb={3}>
                      <FormControl fullWidth>
                        <FieldLabel>API Key</FieldLabel>
                        <StyledTextField
                          placeholder="Enter API key"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          error={!!errors.apiKey}
                          helperText={errors.apiKey}
                          InputProps={
                            isReadOnly ? { readOnly: true } : undefined
                          }
                        />
                      </FormControl>
                    </Box>
                  )}
                </motion.div>
              )}
            </AnimatePresence>

            {/* MODIFIED: Additional Configuration - Now hidden for JIRA */}
            {/* ADDITIONAL CONFIGURATION */}
            {(() => {
              const isSqlBasic =
                connector === 'sql' && authType === 'Basic Authentication';
              const showInstanceFields =
                connector !== 'JIRA' && meta?.requiresInstanceUrl === true;

              // If neither SQL Basic nor instance URL is required, hide the section
              if (!isSqlBasic && !showInstanceFields) return null;

              return (
                <>
                  <MotionSectionTitle
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                  >
                    Additional Configuration
                  </MotionSectionTitle>

                  {isSqlBasic ? (
                    <>
                      {/* Host + Port (same row) */}
                      <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                        gap={2}
                        mb={2}
                      >
                        <FormControl fullWidth error={!!errors.host}>
                          <FieldLabel>Host</FieldLabel>
                          <StyledTextField
                            placeholder="e.g., 127.0.0.1 or localhost"
                            value={host}
                            onChange={(e) => setHost(e.target.value)}
                            error={!!errors.host}
                            helperText={errors.host}
                            InputProps={
                              isReadOnly ? { readOnly: true } : undefined
                            }
                          />
                        </FormControl>

                        <FormControl fullWidth error={!!errors.port}>
                          <FieldLabel>Port</FieldLabel>
                          <StyledTextField
                            placeholder="e.g., 5432"
                            value={port}
                            onChange={(e) =>
                              setPort(e.target.value.replace(/[^\d]/g, ''))
                            }
                            error={!!errors.port}
                            helperText={errors.port}
                            InputProps={
                              isReadOnly ? { readOnly: true } : undefined
                            }
                          />
                        </FormControl>
                      </Box>

                      {/* Database Name */}
                      <Box
                        display="grid"
                        gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                        gap={2}
                        mb={3}
                      >
                        <FormControl error={!!errors.ttl}>
                          <FieldLabel>TTL</FieldLabel>
                          <StyledSelect
                            input={<StyledOutlinedInput />}
                            value={ttl}
                            onChange={(e) => setTtl(e.target.value)}
                            disabled={isReadOnly}
                          >
                            <MenuItem value={300}>3 months</MenuItem>
                            <MenuItem value={600}>6 months </MenuItem>
                            <MenuItem value={1000}>1 year</MenuItem>
                            <MenuItem value={0}>Never</MenuItem>
                          </StyledSelect>
                          <FormHelperText
                            sx={{
                              color: '#ef4444',
                              fontSize: '0.75rem',
                              mt: 0.5,
                            }}
                          >
                            {errors.ttl}
                          </FormHelperText>
                        </FormControl>

                        <FormControl fullWidth error={!!errors.databaseName}>
                          <FieldLabel>Database Name</FieldLabel>
                          <StyledTextField
                            placeholder="e.g., my_database"
                            value={databaseName}
                            onChange={(e) => setDatabaseName(e.target.value)}
                            error={!!errors.databaseName}
                            helperText={errors.databaseName}
                            InputProps={
                              isReadOnly ? { readOnly: true } : undefined
                            }
                          />
                        </FormControl>
                      </Box>
                    </>
                  ) : (
                    // Non-SQL Basic case: only show instance URL–driven fields
                    renderConnectorFields()
                  )}

                  {/* Maximum Insert Record Size - Available for all connector types */}
                  {connector && (
                    <Box
                      display="grid"
                      gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                      gap={2}
                      mb={3}
                    >
                      <FormControl>
                        <FieldLabel>Maximum Insert Record Size</FieldLabel>
                        <StyledTextField
                          placeholder="Enter batch size"
                          value={maxInsertRecords}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d]/g, '');
                            setMaxInsertRecords(value);
                          }}
                          InputProps={
                            isReadOnly ? { readOnly: true } : undefined
                          }
                        />
                        <FormHelperText
                          sx={{
                            color: '#6b7280',
                            fontSize: '0.75rem',
                            mt: 0.5,
                          }}
                        >
                          (Optional) If the record size exceeds the SaaS platform limit, the load may fail.
                        </FormHelperText>
                      </FormControl>
                    </Box>
                  )}
                </>
              );
            })()}

            {/* Maximum Insert Record Size - For connectors without Additional Configuration section */}
            {(() => {
              const isSqlBasic =
                connector === 'sql' && authType === 'Basic Authentication';
              const showInstanceFields =
                connector !== 'JIRA' && meta?.requiresInstanceUrl === true;
              const hasAdditionalConfig = isSqlBasic || showInstanceFields;

              // Show this field if connector is selected but Additional Configuration section is hidden
              if (connector && !hasAdditionalConfig) {
                return (
                  <>
                    <MotionSectionTitle
                      initial={{ opacity: 0, x: -8 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.35, ease: 'easeOut' }}
                    >
                      Additional Configuration
                    </MotionSectionTitle>
                    <Box
                      display="grid"
                      gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }}
                      gap={2}
                      mb={3}
                    >
                      <FormControl>
                        <FieldLabel>Maximum Insert Record Size</FieldLabel>
                        <StyledTextField
                          placeholder="Enter batch size"
                          value={maxInsertRecords}
                          onChange={(e) => {
                            const value = e.target.value.replace(/[^\d]/g, '');
                            setMaxInsertRecords(value);
                          }}
                          InputProps={
                            isReadOnly ? { readOnly: true } : undefined
                          }
                        />
                        <FormHelperText
                          sx={{
                            color: '#6b7280',
                            fontSize: '0.75rem',
                            mt: 0.5,
                          }}
                        >
                          (Optional) If the record size exceeds the SaaS platform limit, the load may fail.
                        </FormHelperText>
                      </FormControl>
                    </Box>
                  </>
                );
              }
              return null;
            })()}

            {/* Reauthentication Password Field */}
            {reauthRequired && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  bgcolor: '#f8fafc',
                  borderRadius: 1,
                  border: '1px solid #e5e7eb',
                }}
              >
                <Typography variant="h6" sx={{ mb: 1, color: '#374151' }}>
                  Reauthentication Required
                </Typography>
                <Typography variant="body2" sx={{ mb: 2, color: '#6b7280' }}>
                  Please enter your password to confirm this update.
                </Typography>
                <StyledTextField
                  fullWidth
                  type="password"
                  value={reauthPassword}
                  onChange={(e) => setReauthPassword(e.target.value)}
                  placeholder="Enter your password"
                  error={!!reauthError}
                  helperText={reauthError}
                  disabled={reauthLoading}
                  sx={{ mb: 2, width: '50%' }}
                />
                <Box display="flex" gap={2}>
                  <StyledButton
                    variant="outlined"
                    onClick={() => {
                      setReauthRequired(false);
                      setReauthPassword('');
                      setReauthError('');
                    }}
                    disabled={reauthLoading}
                  >
                    Cancel
                  </StyledButton>
                  <StyledButton
                    variant="contained"
                    onClick={handleReauthAndUpdate}
                    disabled={reauthLoading || !reauthPassword.trim()}
                    startIcon={
                      reauthLoading ? (
                        <CircularProgress size={16} sx={{ color: '#fff' }} />
                      ) : null
                    }
                  >
                    {reauthLoading ? 'Verifying…' : 'Verify & Update'}
                  </StyledButton>
                </Box>
              </Box>
            )}

            <Box display="flex" justifyContent="flex-end" gap={2} mt={3} pt={2}>
              <StyledButton
                variant="outlined"
                onClick={() => navigate('/myconnections')}
                sx={{ minWidth: 120 }} // optional; compact
              >
                Cancel
              </StyledButton>

              {/* <StyledButton
                variant="contained"
                onClick={
                  editingConnId ? handleUpdateConnection : handleTestConnection
                }
                disabled={reauthLoading}
                sx={{ minWidth: 140 }} // keep a tad wider if you prefer
              >
                {editingConnId
                  ? reauthLoading
                    ? 'Verifying…'
                    : 'Save Changes'
                  : 'Test Connection'}
              </StyledButton> */}

              {isTestMode ? (
                // 🧪 Quick Test button (read-only mode)
                <StyledButton
                  variant="contained"
                  onClick={handleSqlTestConnection}
                  disabled={
                    reauthLoading || !isSqlLikeConnector || !sqlTestToken
                  }
                  sx={{ minWidth: 160 }}
                >
                  Quick Test Connection
                </StyledButton>
              ) : (
                // Normal create / edit behavior
                <StyledButton
                  variant="contained"
                  onClick={
                    editingConnId
                      ? handleUpdateConnection
                      : handleTestConnection
                  }
                  disabled={reauthLoading}
                  sx={{ minWidth: 140 }}
                >
                  {editingConnId
                    ? reauthLoading
                      ? 'Verifying…'
                      : 'Save Changes'
                    : 'Test Connection'}
                </StyledButton>
              )}
            </Box>
            <Dialog
              open={sqlTroubleshootingOpen}
              onClose={() =>
                !updatingHostPort && setSqlTroubleshootingOpen(false)
              }
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle sx={{ fontSize: '1rem', fontWeight: 600 }}>
                Quick Test Troubleshooting
              </DialogTitle>

              <DialogContent dividers sx={{ pt: 1.5 }}>
                <Typography variant="body2" sx={{ mb: 1.5, color: '#4b5563' }}>
                  {sqlTroubleshootingSummary}
                </Typography>

                {sqlTroubleshootingCauses?.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="subtitle2"
                      sx={{ mb: 0.5, color: '#374151', fontWeight: 500 }}
                    >
                      Possible causes:
                    </Typography>
                    <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                      {sqlTroubleshootingCauses.slice(0, 2).map((c, idx) => (
                        <li key={idx}>
                          <Typography
                            variant="body2"
                            sx={{ color: '#6b7280', fontSize: '0.8rem' }}
                          >
                            {c}
                          </Typography>
                        </li>
                      ))}
                    </ul>
                  </Box>
                )}

                <Typography
                  variant="subtitle2"
                  sx={{ mb: 1, color: '#374151', fontWeight: 500 }}
                >
                  Update host &amp; port
                </Typography>

                <Box
                  display="grid"
                  gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }}
                  gap={2}
                >
                  <FormControl fullWidth error={!!dialogHostError}>
                    <FieldLabel>Host</FieldLabel>
                    <StyledTextField
                      value={newHost}
                      onChange={(e) => setNewHost(e.target.value)}
                      placeholder="New host"
                    />
                    {dialogHostError && (
                      <FormHelperText
                        sx={{ color: '#ef4444', fontSize: '0.75rem', mt: 0.5 }}
                      >
                        {dialogHostError}
                      </FormHelperText>
                    )}
                  </FormControl>

                  <FormControl fullWidth error={!!dialogPortError}>
                    <FieldLabel>Port</FieldLabel>
                    <StyledTextField
                      value={newPort}
                      onChange={(e) =>
                        setNewPort(e.target.value.replace(/[^\d]/g, ''))
                      }
                      placeholder="New port"
                    />
                    {dialogPortError && (
                      <FormHelperText
                        sx={{ color: '#ef4444', fontSize: '0.75rem', mt: 0.5 }}
                      >
                        {dialogPortError}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Box>
              </DialogContent>

              <DialogActions sx={{ p: 1.5, pr: 2 }}>
                <StyledButton
                  variant="outlined"
                  onClick={() => setSqlTroubleshootingOpen(false)}
                  disabled={updatingHostPort}
                >
                  Close
                </StyledButton>
                <StyledButton
                  variant="contained"
                  onClick={handleUpdateHostPort}
                  disabled={updatingHostPort}
                  startIcon={
                    updatingHostPort ? (
                      <CircularProgress size={16} sx={{ color: '#fff' }} />
                    ) : null
                  }
                >
                  {updatingHostPort ? 'Updating…' : 'Save Host & Port'}
                </StyledButton>
              </DialogActions>
            </Dialog>
          </FormSection>
        </Box>
      </Box>
    </Box>
  );
};

export default CreateNewConnections;
